import { Grid, makeStyles } from "@material-ui/core";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import MaintainenenceTableDataGrid from "./MaintainenenceTableDataGrid";
import * as d3 from "d3";
import {
  selectQuotaData,
  selectRampStatus,
  selectTeams,
  selectUpdatedRecords,
  clearUpdateQuotaData,
  selectTeamHierarchy,
} from "./redux/management.slice";
import { bulkUpsertQuotaData, getTeamAndFiscalQuarters } from "./redux/management.thunk";
import LoadingData from "./LoadingData";
import { _isNaN } from "../../util/customFunctions";
import { store } from "../../util/store";

const useStyle = makeStyles((theme) => ({
  tableHeader: {
    fontSize: "0.85rem",
    background: "#4472c4",
    paddingRight: "10px",
    width: "7rem",
    fontWeight: 600,
    color: "white",
  },
  tableCell: {
    fontSize: "1rem",
    whiteSpace: "normal",
    fontSize: "0.85rem",
    fontWeight: 400,
    width: "7rem",
  },
}));

const QuotaTabPanel = ({
  data,
  setTouched,
  tab,
  handleSubmit,
  udpatedRecords,
  columnDefs,
  domLayout,
  gridStyle = { margin: "1rem 0rem" },
  teams,
  rampStatus,
}) => {
  if (data === null) {
    return <LoadingData />;
  }

  return (
    <Grid container item justifyContent="center" style={gridStyle}>
      <MaintainenenceTableDataGrid
        setTouched={setTouched}
        tab={tab}
        data={data}
        handleSubmit={handleSubmit}
        udpatedRecords={udpatedRecords}
        domLayout={domLayout}
        columnDefs={columnDefs}
        teams={teams}
        rampStatus={rampStatus}
      />
    </Grid>
  );
};

const RepQuotaPanel = ({ isDryRun }) => {
  const state = store.getState();
  const Currency_Symbol = state.app.user.Currency;
  const classes = useStyle();
  const [touched, setTouched] = useState(false);
  const dispatch = useDispatch();
  const quotaData = useSelector(selectQuotaData);
  const teams = useSelector(selectTeams);
  const rampStatus = useSelector(selectRampStatus);
  const udpatedRecords = useSelector(selectUpdatedRecords);
  const teamHierarchy = useSelector(selectTeamHierarchy);

  // Helper function to flatten hierarchical team data with indentation
  const flattenTeamHierarchy = (hierarchy) => {
    const result = [];

    const flatten = (nodes, indent = 0) => {
      if (!nodes || !Array.isArray(nodes)) return;

      nodes.forEach(node => {
        // Skip the root "All" node, but add all other Parent values
        if (node.Parent && node.Parent !== "All") {
          // Create indentation using whitespace for visual hierarchy
          const indentPrefix = '\u00A0\u00A0\u00A0\u00A0'.repeat(indent);
          result.push({
            value: node.Parent,  // Actual team name for data
            label: `${indentPrefix}${node.Parent}`,  // Indented label for display
          });
        }

        // Recursively flatten nested teams with increased indent
        if (node.teams && node.teams.length > 0) {
          // Only increase indent if current node is not "All"
          flatten(node.teams, node.Parent === "All" ? indent : indent + 1);
        }
      });
    };

    flatten(hierarchy);
    return result;
  };

  console.log(teamHierarchy, 'teamHierarchy');
  console.log("Old_Teams", teams);
  

  // Access the filters array from the nested structure
  const filters = teamHierarchy?.parentChildrenTeamFilter?.filters || [];
  const allTeams = flattenTeamHierarchy(filters);

  console.log(allTeams, 'flattened teams with indentation');
    

  useEffect(() => {
    return () => {
      dispatch(clearUpdateQuotaData());
    };
  }, [dispatch]);

  // Fetch team and fiscal quarters data
  useEffect(() => {
    dispatch(getTeamAndFiscalQuarters({ type: 'rep' }))
  }, [dispatch]);

  const handleSubmit = (updatedRecords) => {
    // Transform updatedRecords into bulk upsert format
    // Group by userId to create one object per rep
    const repMap = {};

    updatedRecords.forEach(record => {
      const userId = record.UserId;
      if (!repMap[userId]) {
        repMap[userId] = {
          hireDate: record.Hire_date || null,
          team: record.Team_Assignment || record.newTeam_Assignment,
          quotaAssignments: []
        };
      }

      repMap[userId].quotaAssignments.push({
        fiscalQuarter: record.FiscalQuarter,
        rampStatus: record.Ramp_Status || record.newRamp_Status,
        NQP_Goal: Math.round(Number(record.NQP_GOAL || record.newNQP_GOAL)),
        userId: record.UserId,
        quota: Math.round(Number(record.Quota || record.newQuota)),
        team: record.Team_Assignment || record.newTeam_Assignment,
        fullName: record.FullName,
        sfdcOwnerId: record.SFDC_Owner_ID,
        canUpsert: true  // Set to true to allow upsert
      });
    });

    const bulkQuotaData = Object.values(repMap);
    dispatch(bulkUpsertQuotaData({ bulkQuotaData }));
  };

  const editBackgroundTeam_Assignment = { "edit-bg": (params) => params.data.Team_Assignment_Updated };
  const editBackgroundQuota = { "edit-bg": (params) => params.data.Quota_Updated };
  const editBackgroundRamp_Status = { "edit-bg": (params) => params.data.Ramp_Status_Updated };
  const editBackgroundNQP_GOAL = { "edit-bg": (params) => params.data.NQP_GOAL_Updated };

  const repColumnDefs = [
    { field: "FullName", sortable: true, filter: true, cellStyle: { textAlign: "left" }, cellClass: classes.tableCell, headerClass: classes.tableHeader, flex: 2.4 },
    { field: "FiscalQuarter", sortable: true, filter: true, type: "rightAligned", headerClass: classes.tableHeader, flex: 1 },
    { field: "Team_Assignment", headerName: "Team", sortable: true, filter: true, cellStyle: { textAlign: "left" }, cellClass: classes.tableCell, headerClass: classes.tableHeader, cellClassRules: editBackgroundTeam_Assignment, cellEditor: "agSelectCellEditor", cellEditorParams: { values: teams }, flex: 2.2 },
    { field: "Quota", sortable: true, filter: true, tooltipField: "Quota", valueSetter: (params) => { if (_isNaN(params.newValue) || parseInt(params.newValue) < 0 || params.newValue === "") { window.alert("Quota must be a valid positive number"); return false; } params.data.Quota = params.newValue; return true; }, cellEditorPopup: true, headerClass: classes.tableHeader, editable: true, type: "rightAligned", flex: 1.3, cellClassRules: editBackgroundQuota, valueFormatter: (x) => `${Currency_Symbol} ${d3.format(",")(Math.round(x.value))}` },
    { field: "NQP_GOAL", headerName: "NQP Goal", sortable: true, filter: true, tooltipField: "NQP_GOAL", headerClass: classes.tableHeader, editable: true, valueSetter: (params) => { if (_isNaN(params.newValue) || parseInt(params.newValue) < 0) { window.alert("NQP goal must be a positive number"); return false; } params.data.NQP_GOAL = params.newValue; return true; }, cellClassRules: editBackgroundNQP_GOAL, type: "rightAligned", flex: 1.8, valueFormatter: (x) => `${Currency_Symbol} ${d3.format(",")(Math.round(x.value))}` },
    { field: "Ramp_Status", headerName: "Ramp Status", sortable: true, filter: true, cellStyle: { textAlign: "left" }, cellClass: classes.tableCell, headerClass: classes.tableHeader, editable: true, cellClassRules: editBackgroundRamp_Status, cellEditor: "agSelectCellEditor", flex: 1.3, cellEditorParams: { values: rampStatus }, resizable: false },
  ];

  return (
    <QuotaTabPanel
      data={quotaData ? quotaData.map((it, idx) => ({ ...it, id: idx })) : null}
      setTouched={setTouched}
      tab="rep"
      handleSubmit={handleSubmit}
      udpatedRecords={udpatedRecords}
      columnDefs={repColumnDefs}
      teams={allTeams}
      rampStatus={rampStatus}
    />
  );
};

export default RepQuotaPanel;
